/* =========================================================
   ESSE WERKS — CONFIGURATOR STATE MODULE
   Single source of truth for the multi-step build configurator.
   Persists state across pages via sessionStorage + URL params.

   Public API (window.ewConfigurator):
     get()              — current state (defensive copy)
     set(partial)       — deep-merge, persist, fire change event
     clear()            — wipe state
     canAdvance(step)   — boolean gate for navigation
     toCartLineItems()  — array of /cart/add.js payloads
     fromUrl()          — rehydrate from URL on demand
     saveToCustomer()   — persist for logged-in user (Phase 5 stub —
                          localStorage interim, Phase 7 wires the
                          real customer.metafields.ew_.saved_configurations
                          write via Customer Account API / app proxy)

   State shape: see Phase 5 spec checkpoint 5.A.
   ========================================================= */
(function () {
  'use strict';

  var STORAGE_KEY      = 'ew_config';
  var EXPIRATION_MS    = 24 * 60 * 60 * 1000; // 24 hours
  var CHANGE_EVENT     = 'ew:configurator-change';

  /* ---- Helpers ---- */
  function uuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  function nowIso() { return new Date().toISOString(); }
  function defensiveCopy(o) { return JSON.parse(JSON.stringify(o)); }

  function isExpired(state) {
    if (!state || !state.meta || !state.meta.updated_at) return false;
    var t = new Date(state.meta.updated_at).getTime();
    if (isNaN(t)) return true;
    return (Date.now() - t) > EXPIRATION_MS;
  }

  function defaultState() {
    return {
      build_id: null,
      vehicle: {},
      stage:   null,
      options: [],
      meta: {
        created_at:   null,
        updated_at:   null,
        current_step: 'entry',
      },
    };
  }

  function hasMeaningfulState(s) {
    return !!(s.vehicle && Object.keys(s.vehicle).length > 0)
        || !!(s.stage)
        || !!(s.options && s.options.length > 0);
  }

  /* ---- Deep merge (objects merge, arrays replace) ---- */
  function merge(target, source) {
    if (!source) return target;
    Object.keys(source).forEach(function (k) {
      var sv = source[k];
      if (sv === undefined) return;
      if (sv === null)                        { target[k] = null; return; }
      if (Array.isArray(sv))                  { target[k] = sv.slice(); return; }
      if (typeof sv === 'object') {
        if (!target[k] || typeof target[k] !== 'object' || Array.isArray(target[k])) target[k] = {};
        merge(target[k], sv);
        return;
      }
      target[k] = sv;
    });
    return target;
  }

  /* ---- sessionStorage I/O ---- */
  function readSession() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) { return null; }
  }
  function writeSession(state) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
  }
  function clearSession() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }

  /* ---- URL <-> state encoding
     Mirrors only the navigation-critical fields so URLs stay readable
     and shareable links pick the recipient up at the same step. ---- */
  var URL_KEYS = ['chassis','year','make','model','trim','engine','entry','vin',
                  'stage_level','stage_variant','options','step','build_id','updated_at','b'];

  /* ---- ?b= full-fidelity share serializer (GARAGE-1) ----
     A build round-trips through one URL-safe-base64 blob carrying the ENTIRE
     _config_state JSON (incl. price_cents) plus a livery snapshot. We reuse
     JSON.stringify(state) — the SAME serialization _config_state uses — and
     wrap it as { state, livery } so the state half flows through the same
     rehydrate path fromSavedState uses ({state} unwrap). */
  function b64UrlEncode(str) {
    /* UTF-8-safe: model/trim strings may carry non-Latin-1 chars. */
    var b64 = btoa(unescape(encodeURIComponent(str)));
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function b64UrlDecode(s) {
    var b64 = String(s).replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return decodeURIComponent(escape(atob(b64)));
  }
  function decodeShareParam() {
    var raw;
    try { raw = new URLSearchParams(window.location.search).get('b'); } catch (_) { return null; }
    if (!raw) return null;
    var obj;
    try { obj = JSON.parse(b64UrlDecode(raw)); } catch (_) { return null; }
    if (!obj || typeof obj !== 'object') return null;
    var inner  = (obj.state  && typeof obj.state  === 'object') ? obj.state  : obj;
    var livery = (obj.livery && typeof obj.livery === 'object') ? obj.livery : null;
    return { state: inner, livery: livery };
  }
  function liverySnapshot() {
    var L = window.EWLivery;
    if (!L) return { livery: null, useCase: null };
    return {
      livery:  (typeof L.read === 'function')        ? (L.read() || null)        : null,
      useCase: (typeof L.readUseCase === 'function') ? (L.readUseCase() || null) : null
    };
  }
  /* ew-livery.js defers AFTER this module, so at init() window.EWLivery may
     not exist — wait for DOMContentLoaded, by which point every deferred
     script (incl. ew-livery.js) has executed. */
  function applyShareLivery(snap) {
    if (!snap || (!snap.useCase && !snap.livery)) return;
    function doApply() {
      var L = window.EWLivery;
      if (!L) return;
      if (snap.useCase && typeof L.setUseCase === 'function') L.setUseCase(snap.useCase);
      else if (snap.livery && typeof L.set === 'function')    L.set(snap.livery);
    }
    if (window.EWLivery) { doApply(); }
    else if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', doApply, { once: true });
    } else {
      setTimeout(doApply, 0);
    }
  }
  /* toShareUrl — a fully-restorable share link. Reuses get()'s defensive copy
     and the _config_state JSON serialization; bundles the livery snapshot. */
  function toShareUrl(state) {
    var s = state || get();
    var payload = { state: s, livery: liverySnapshot() };
    var encoded = b64UrlEncode(JSON.stringify(payload));
    var origin  = (window.location && window.location.origin) || '';
    return origin + '/pages/configure?b=' + encoded;
  }
  /* toSummary — compact card/Klaviyo/label projection. total_cents mirrors the
     canonical build total (configure-review.liquid): stage.price_cents +
     Σ option.price_cents × qty. Prices are carried IN the state, so this is
     exact and matches across a ?b= round-trip. */
  function toSummary(state) {
    var s = state || get();
    var v = s.vehicle || {};
    var st = s.stage || null;
    var opts = s.options || [];
    var totalCents = (st && st.price_cents) ? st.price_cents : 0;
    opts.forEach(function (o) { totalCents += (o.price_cents || 0) * (o.quantity || 1); });
    return {
      chassis:      v.code || v.chassis_code || v.chassis || null,
      year:         v.year || null,
      model:        v.model || null,
      stage:        st ? (st.stage_level || null) : null,
      option_count: opts.length,
      total_cents:  totalCents
    };
  }

  function readUrl() {
    /* GARAGE-1: a ?b= full-fidelity blob WINS over flat params — it carries the
       entire _config_state (incl. price_cents + livery), so the recipient lands
       on an identical build. Flat params stay the human-readable/SEO fallback. */
    _pendingShareLivery = null;
    _fromShare = false;
    var share = decodeShareParam();
    if (share && share.state) {
      _pendingShareLivery = share.livery;
      _fromShare = true;
      return share.state;
    }

    var params  = new URLSearchParams(window.location.search);
    var state   = defaultState();
    var hasAny  = false;

    function setIf(p, fn) { if (params.has(p)) { fn(params.get(p)); hasAny = true; } }

    setIf('chassis',       function (v) { state.vehicle.code = v; });
    setIf('year',          function (v) { state.vehicle.year = parseInt(v, 10) || null; });
    setIf('make',          function (v) { state.vehicle.make = v; });
    setIf('model',         function (v) { state.vehicle.model = v; });
    setIf('trim',          function (v) { state.vehicle.trim = v; });
    setIf('engine',        function (v) { state.vehicle.engine_code = v; });
    setIf('entry',         function (v) { state.vehicle.entry_mode = v; });
    setIf('vin',           function (v) { state.vehicle.vin = v; });
    setIf('stage_level',   function (v) { state.stage = state.stage || {}; state.stage.stage_level = v; });
    setIf('stage_variant', function (v) {
      state.stage = state.stage || {};
      state.stage.variant_id = parseInt(v, 10) || null;
    });
    if (params.has('options')) {
      var raw = params.get('options');
      state.options = raw.split(',').map(function (s) {
        var parts = s.split(':');
        return {
          product_handle: parts[0] || '',
          variant_id:     parseInt(parts[1], 10) || null,
          quantity:       parseInt(parts[2], 10) || 1,
        };
      }).filter(function (o) { return o.variant_id; });
      if (state.options.length) hasAny = true;
    }
    setIf('step',       function (v) { state.meta.current_step = v; });
    setIf('build_id',   function (v) { state.build_id = v; });
    setIf('updated_at', function (v) { state.meta.updated_at = v; });

    return hasAny ? state : null;
  }

  function writeUrl(state) {
    var params = new URLSearchParams(window.location.search);
    URL_KEYS.forEach(function (k) { params.delete(k); });

    if (state.vehicle.code)  params.set('chassis', state.vehicle.code);
    if (state.vehicle.year)          params.set('year', state.vehicle.year);
    if (state.vehicle.make)          params.set('make', state.vehicle.make);
    if (state.vehicle.model)         params.set('model', state.vehicle.model);
    if (state.vehicle.trim)          params.set('trim', state.vehicle.trim);
    if (state.vehicle.engine_code)   params.set('engine', state.vehicle.engine_code);
    if (state.vehicle.entry_mode)    params.set('entry', state.vehicle.entry_mode);
    if (state.vehicle.vin)           params.set('vin', state.vehicle.vin);
    if (state.stage && state.stage.stage_level)  params.set('stage_level', state.stage.stage_level);
    if (state.stage && state.stage.variant_id)   params.set('stage_variant', state.stage.variant_id);
    if (state.options && state.options.length > 0) {
      params.set('options', state.options.map(function (o) {
        return [o.product_handle || '', o.variant_id || '', o.quantity || 1].join(':');
      }).join(','));
    }
    if (state.meta && state.meta.current_step && state.meta.current_step !== 'entry') {
      params.set('step', state.meta.current_step);
    }
    if (state.build_id)              params.set('build_id', state.build_id);
    if (state.meta && state.meta.updated_at) params.set('updated_at', state.meta.updated_at);

    var qs = params.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    try { window.history.replaceState({}, '', newUrl); } catch (_) {}
  }

  /* ---- State container ---- */
  var _state = null;
  var _pendingShareLivery = null;  /* livery snapshot awaiting restore (GARAGE-1) */
  var _fromShare = false;          /* last readUrl() consumed a ?b= blob (GARAGE-1) */

  function init() {
    var urlState     = readUrl();
    var sessionState = readSession();

    /* A ?b= share link is a deliberate artifact (share / CTA pre-seed / anon-
       email) — exempt from the 24h staleness gate that governs abandoned
       sessions. Flat-param + session states still expire. */
    var urlOk     = urlState     && (_fromShare || !isExpired(urlState));
    var sessionOk = sessionState && !isExpired(sessionState);

    if (!urlOk && !sessionOk) {
      _state = defaultState();
      clearSession();
      return;
    }
    if (urlOk && sessionOk) {
      /* Merge session under URL — URL wins on overlap (explicit nav signal). */
      _state = defaultState();
      merge(_state, sessionState);
      merge(_state, urlState);
    } else if (urlOk) {
      _state = defaultState();
      merge(_state, urlState);
    } else {
      _state = defaultState();
      merge(_state, sessionState);
    }
    if (!_state.meta) _state.meta = { created_at: null, updated_at: null, current_step: 'entry' };
    applyShareLivery(_pendingShareLivery);
    _pendingShareLivery = null;
  }

  function persist() {
    if (!_state.build_id && hasMeaningfulState(_state)) {
      _state.build_id = uuid();
    }
    if (!_state.meta) _state.meta = { created_at: null, updated_at: null, current_step: 'entry' };
    if (!_state.meta.created_at) _state.meta.created_at = nowIso();
    _state.meta.updated_at = nowIso();
    writeSession(_state);
    writeUrl(_state);
  }

  function fireChange() {
    try {
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: defensiveCopy(_state) }));
    } catch (_) {
      /* Older browsers — silent. */
    }
  }

  /* ---- Public API ---- */
  function get() { return defensiveCopy(_state); }

  function set(partial) {
    merge(_state, partial || {});
    persist();
    fireChange();
    return get();
  }

  function clear() {
    _state = defaultState();
    clearSession();
    /* Strip our URL params */
    var params = new URLSearchParams(window.location.search);
    URL_KEYS.forEach(function (k) { params.delete(k); });
    var qs = params.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    try { window.history.replaceState({}, '', newUrl); } catch (_) {}
    fireChange();
  }

  /* canAdvance — minimum data needed to safely render each step. */
  function canAdvance(toStep) {
    var s = _state;
    /* Migrate-on-read: canonical `code`, legacy chassis_code / chassis
       fallback for a state hydrated from an older session/URL. */
    var vc = s.vehicle && (s.vehicle.code || s.vehicle.chassis_code || s.vehicle.chassis);
    switch (toStep) {
      case 'entry':   return true;
      case 'stage':   return !!vc;
      case 'options': return !!vc
                          && !!(s.stage   && s.stage.variant_id);
      case 'vin':
      case 'identify':
                      return !!vc
                          && !!(s.stage   && s.stage.variant_id);
      case 'review':  return !!vc
                          && !!(s.stage   && s.stage.variant_id)
                          && !!(s.vehicle.vin);
      default:        return false;
    }
  }

  /* Human-friendly stage label from stage_level slug. */
  function stageLabel(slug) {
    if (!slug) return '';
    /* stage_2_plus → "Stage 2 plus" → "Stage 2+" approximation */
    return slug
      .replace(/_plus/g, '+')
      .split('_')
      .map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); })
      .join(' ');
  }

  /* Human stage label for a STANDALONE label/card (e.g. "Stage 2").
     Distinct from stageLabel(), which feeds the cart line property whose
     KEY is already "Stage" so its value must stay bare. Canonical tune
     tokens are s1/s2/s3 (variant metafield ew_.stage_level); be defensive
     about stage_2 / 2 / 2_plus forms too. */
  function stageDisplay(lvl) {
    if (!lvl) return '';
    var s = String(lvl).trim();
    var m = s.match(/^s(\d+)$/i);            // s1/s2/s3 -> "Stage N"
    if (m) return 'Stage ' + m[1];
    var pretty = stageLabel(s);              // stage_2 -> "Stage 2"; 2_plus -> "2+"; 2 -> "2"
    return /stage/i.test(pretty) ? pretty : ('Stage ' + pretty);
  }

  /* GARAGE-4: compact saved-build label ("G80 M3 · Stage 2"). Used to enrich
     the persisted record so /pages/my-builds can render a heading without
     re-deriving it from raw state. Uses stageDisplay for a legible stage
     segment (NOT stageLabel, whose bare value feeds the cart "Stage" key). */
  function buildLabel(s) {
    if (!s) return '';
    var v = s.vehicle || {};
    var head = [v.code || v.chassis_code || v.chassis, v.model].filter(Boolean).join(' ');
    var st = (s.stage && s.stage.stage_level) ? stageDisplay(s.stage.stage_level) : '';
    return [head, st].filter(Boolean).join(' · ');
  }

  /* Format the human-readable Vehicle property value posted to the cart.
     GATE1-1 F-12 — the value shows the engine FAMILY ("B48"), matching the
     canonical EWVehicleResolver.cartVehicleProperty form the Tune Builder
     path writes — never the full variant code ("B48B20O1"). Display value
     only: the full engine_code persists untouched inside _config_state. */
  function vehicleLabel(v) {
    if (!v) return '';
    var head = [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ');
    var engFam = (window.EWVehicleResolver && window.EWVehicleResolver.engineFamily)
      ? window.EWVehicleResolver.engineFamily(v.engine_code || '')
      : (v.engine_code || '');
    var tail = [v.code || v.chassis_code || v.chassis, engFam].filter(Boolean).join(' · ');
    return tail ? (head ? head + ' · ' + tail : tail) : head;
  }

  function toCartLineItems() {
    var s = _state;
    if (!s.stage || !s.stage.variant_id) return [];
    var build_id = s.build_id || uuid();

    /* Stage line — carries the visible (non-_underscore) properties.
       Also carries a hidden _config_state blob (the full state JSON)
       so the cart's "Edit Build" action can rehydrate the configurator
       without re-deriving fields from line item properties. */
    var stageProps = {
      '_build_id':     build_id,
      '_config_state': JSON.stringify(s)
    };
    var vLabel = vehicleLabel(s.vehicle);
    if (vLabel)               stageProps['Vehicle'] = vLabel;
    if (s.vehicle && s.vehicle.vin)
                              stageProps['VIN']     = s.vehicle.vin;
    var label = stageLabel(s.stage.stage_level);
    if (label)                stageProps['Stage']   = label;
    /* Identify-step extras — visible in cart + admin so the shop has
       full context on tune delivery date / car state. */
    if (s.vehicle && s.vehicle.production_date)
                              stageProps['Production date'] = s.vehicle.production_date;
    if (s.vehicle && s.vehicle.current_mileage)
                              stageProps['Mileage']         = String(s.vehicle.current_mileage) + ' mi';
    if (s.vehicle && s.vehicle.current_mods)
                              stageProps['Current mods']    = s.vehicle.current_mods;

    var lines = [{
      id: s.stage.variant_id,
      quantity: 1,
      properties: stageProps,
    }];

    (s.options || []).forEach(function (o) {
      if (!o.variant_id) return;
      lines.push({
        id: o.variant_id,
        quantity: o.quantity || 1,
        properties: { '_build_id': build_id },
      });
    });

    return lines;
  }

  function fromUrl() {
    var u = readUrl();
    if (!u || (!_fromShare && isExpired(u))) return null;
    _state = defaultState();
    merge(_state, u);
    if (!_state.meta) _state.meta = { created_at: null, updated_at: null, current_step: 'entry' };
    writeSession(_state);
    applyShareLivery(_pendingShareLivery);
    _pendingShareLivery = null;
    fireChange();
    return get();
  }

  /* Persists current state to the saved-builds list. Delegates to
     window.ewCustomer.saveBuild so writes go through one canonical path
     (with build_id assignment so /pages/my-builds can resume + delete).
     Falls back to a direct localStorage write if ewCustomer hasn't
     loaded yet (defensive — both scripts defer-load from the same head). */
  function saveToCustomer(name, build) {
    var customerId = (typeof window.ewCustomerId === 'number') ? window.ewCustomerId : null;
    if (!customerId) {
      return Promise.reject(new Error('Not signed in'));
    }
    /* CS-3a: accept an OPTIONAL explicit build. Default (no build arg) is
       byte-identical to before (src === _state). An explicit build lets a host
       (e.g. Tune Builder) persist a snapshotted build without mutating live
       configurator state or the page URL — these are all pure reads. */
    var src = (build && typeof build === 'object') ? build : _state;
    /* GARAGE-4: persist an enriched record so /pages/my-builds can render a
       card (label, summary, total, livery + a fully-restorable entry URL)
       without re-deriving from raw state. Built BEFORE the branch so the
       delegate (ewCustomer.saveBuild) and the localStorage fallback persist
       the SAME object. toShareUrl()/toSummary() read `src` (default: current
       state); liverySnapshot() reads window.EWLivery (stays current-livery). */
    var summary = toSummary(src);
    var entry = {
      saved_at:    nowIso(),
      state:       defensiveCopy(src),
      entry:       toShareUrl(src),
      label:       buildLabel(src),
      summary:     summary,
      total_cents: summary.total_cents,
      livery:      liverySnapshot()
    };
    /* GARAGE-5: optional save-time name (complements the on-card Rename).
       Leave the key ABSENT when blank — the card heading falls back to the
       spec label. Set before the branch so delegate + fallback both carry it. */
    if (name != null && String(name).trim()) entry.name = String(name).trim();
    if (window.ewCustomer && window.ewCustomer.saveBuild) {
      return window.ewCustomer.saveBuild(entry);
    }
    try {
      var key = 'ew_saved_configurations:' + customerId;
      var existing = JSON.parse(localStorage.getItem(key) || '[]');
      entry.build_id = 'b_' + Math.random().toString(36).slice(2, 10) + '_' + Date.now().toString(36);
      existing.push(entry);
      /* GARAGE-4: cap-consistency — match the delegate's 10-build cap
         (ew-customer.js saveBuild) so the fallback path can't grow unbounded. */
      if (existing.length > 10) existing = existing.slice(-10);
      localStorage.setItem(key, JSON.stringify(existing));
      return Promise.resolve({ ok: true, persisted: 'localStorage', count: existing.length, build_id: entry.build_id });
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /* Boot — runs at script load (defer ensures DOM available but we don't use it). */
  init();

  /* rehydrate — replaces internal state wholesale (used by the cart's
     Edit Build action to restore a previously-added build's state). */
  function rehydrate(newState) {
    if (!newState || typeof newState !== 'object') return null;
    _state = defaultState();
    merge(_state, newState);
    if (!_state.meta) _state.meta = { created_at: null, updated_at: null, current_step: 'entry' };
    _state.meta.updated_at = nowIso();
    writeSession(_state);
    writeUrl(_state);
    fireChange();
    return get();
  }

  /* fromSavedState — rehydrate from a saved-build entry (Phase 7 Resume).
     Saved entries may be wrapped as { saved_at, state } (localStorage shape)
     or stored bare (server metafield shape). Accepts both. */
  function fromSavedState(saved) {
    if (!saved || typeof saved !== 'object') return null;
    var inner = (saved.state && typeof saved.state === 'object') ? saved.state : saved;
    var result = rehydrate(inner);
    /* GARAGE-4: restore the saved livery snapshot (enriched records carry
       livery:{livery,useCase}). Reuses applyShareLivery so saved-record
       resume and ?b= share-link resume are symmetric. Legacy records
       (no livery) -> applyShareLivery's guard no-ops. */
    if (saved.livery) applyShareLivery(saved.livery);
    return result;
  }

  window.ewConfigurator = {
    get:              get,
    set:              set,
    clear:            clear,
    canAdvance:       canAdvance,
    toCartLineItems:  toCartLineItems,
    toShareUrl:       toShareUrl,
    toSummary:        toSummary,
    buildLabel:       buildLabel,
    fromUrl:          fromUrl,
    rehydrate:        rehydrate,
    fromSavedState:   fromSavedState,
    saveToCustomer:   saveToCustomer,
    /* Exposed constants for the configurator pages to read. */
    _CHANGE_EVENT:    CHANGE_EVENT,
    _STORAGE_KEY:     STORAGE_KEY,
  };
})();
